import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CropLandscapeOutlinedIcon from '@mui/icons-material/CropLandscapeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import ViewListIcon from '@mui/icons-material/ViewList';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { printPalletLabels } from '../../../store/store';
import { printHtmlDocument } from '../../../tools/printDocument';
import DocPreviewFrame from '../../../tools/docPreviewFrame';
import CopyLinkButton from '../../main/copyLinkButton';
import { useDocFormat, useDocLanguage } from '../docFormat';
import { PalletLabelCard } from './palletLabelButton';
import PalletPrintDialog from './palletPrintDialog';
import ShortLabelSheetDialog from './shortLabelSheetDialog';

const PHONE_ICON = { width: 40, height: 40 };
const PALLET_COLS = 'minmax(70px, 0.9fr) minmax(40px, 0.5fr) minmax(90px, 1.4fr) 44px 64px 90px minmax(96px, 1fr)';
const PALLET_COLS_XS = 'minmax(0, 1fr) minmax(0, 0.55fr) minmax(0, 0.9fr) minmax(0, 1.1fr)';
const byRecord = (p) => p._id;

function Meta({ label, value, T, mono }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: T.TEXT_TER, mb: 0.15 }}>{label}</Typography>
      <Typography dir={mono ? 'ltr' : undefined} sx={{ fontSize: '0.78rem', color: T.TEXT_PRI, textAlign: 'start', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
  );
}

const sumOf = (pallets) => pallets.reduce((acc, p) => {
  (p.items || []).forEach((it) => { acc.pcs += Number(it.pcs) || 0; acc.sqm += Number(it.sqm) || 0; });
  return acc;
}, { pcs: 0, sqm: 0 });

// A label form's record, in the same shell as a packing list's. It opens as the DOCUMENT: the form on A4 (GET /label-forms/:id/html, the
// same answer Print sends to the printer), and under it the form's labels, one card each, printed with the options a label always had,
// exactly as under a packing list. The Data view holds the form and its labels, each with its state.
export default function LabelFormDetail({ formId, refreshKey, canPrint, canEdit, canDelete, canList, colors, calendar, onBack, onEdit, onDelete, onMakeList, onOpenPallet, onOpenList, T }) {
  const { t } = useTranslation();
  const fmt = useDocFormat();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);

  const [form, setForm] = useState(null);
  const [missing, setMissing] = useState(false);
  const [view, setView] = useState('document');      // 'document' | 'data'
  const [lang, setLang] = useDocLanguage(formId);
  const [docHtml, setDocHtml] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [printAllOpen, setPrintAllOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setForm((cur) => (cur && cur._id === formId ? cur : null)); setMissing(false);
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/${formId}` })
      .then((res) => { if (!cancelled) setForm(res.data); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [formId, refreshKey, authCtx, axiosGlobal]);

  // The document on screen belongs to one form, one language and one refresh: another form's document never stays up under this one's
  // header while the fetch runs, and a slower earlier answer never replaces a newer one.
  const docFor = useRef('');
  useEffect(() => {
    const key = `${formId}|${lang}|${refreshKey}`;
    docFor.current = key;
    setDocHtml(''); setDocLoading(true);
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/${formId}/html`, params: { lang }, responseType: 'text' })
      .then((res) => { if (docFor.current === key) setDocHtml(typeof res.data === 'string' ? res.data : ''); })
      .catch(() => { if (docFor.current === key) setDocHtml(''); })
      .finally(() => { if (docFor.current === key) setDocLoading(false); });
  }, [formId, lang, refreshKey, authCtx, axiosGlobal]);

  const pallets = useMemo(() => (form && form._id === formId ? form.pallets || [] : []), [form, formId]);
  const ids = useMemo(() => pallets.map((p) => p._id), [pallets]);

  if (missing) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC }}>{t('mis.lfNotFound')}</Typography></Box>;
  }
  if (!form || form._id !== formId) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={20} /></Box>;

  const sum = sumOf(pallets);
  const free = pallets.filter((p) => !p.list);
  // the preview on screen IS the document: Print hands it over as it is
  const printDocument = () => { if (docHtml) printHtmlDocument(docHtml); };
  // one label, as a packing list's card prints it: the slab label in the document's language, or the short label on a page of its own
  const printOne = (pallet) => (kind) => dispatch(printPalletLabels({
    authCtx, axiosGlobal, branchId: form.branchId, ids: [pallet._id], kind: kind === 'short' ? 'strip' : 'slab', lang,
  }));
  const printSheet = (keys, paper) => dispatch(printPalletLabels({ authCtx, axiosGlobal, branchId: form.branchId, ids: keys, kind: 'short', paper }));

  const meta = [
    [t('mis.lfNumberField'), `#${form.docNumber}`, true],
    [t('mis.lfDateField'), fmt.date(form.issueDate, form.dateSystem)],
    [t('mis.originAddressField'), form.originAddress],
    [t('mis.destinationAddressField'), form.destinationAddress],
    [t('mis.lblPalletizedBy'), form.palletizedBy],
    [t('mis.lblCreatedBy'), form.createdByName],
    [t('mis.sectionNotes'), form.notes],
  ].filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
  const cols = isXs ? PALLET_COLS_XS : PALLET_COLS;
  const head = (l, end) => (
    <Typography key={l} sx={{ minWidth: 0, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: T.TEXT_TER, textAlign: end ? 'end' : 'start' }}>{l}</Typography>
  );
  const numberCell = { fontSize: '0.73rem', textAlign: 'end', fontVariantNumeric: 'tabular-nums', color: T.TEXT_SEC, minWidth: 0 };
  const sectionLabel = { fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: T.TEXT_TER };
  const labelButton = { fontSize: '0.7rem', textTransform: 'none', height: 28, ...(isXs ? { height: 'auto', minHeight: 44, fontSize: '0.8rem', px: 1.5 } : {}) };

  return (
    <Box data-testid="lf-detail" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={onBack} aria-label={t('common.back')} sx={isXs ? PHONE_ICON : undefined}>
          <ArrowBackIcon sx={{ fontSize: isXs ? 22 : 18, color: T.TEXT_SEC }} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography data-testid="lf-detail-title" sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>
            {t('mis.lfDetailTitle', { number: form.docNumber })}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>
            {[fmt.date(form.issueDate, form.dateSystem), t('mis.lfLabelCount', { count: pallets.length }), t('mis.plTotalsSummary', { pcs: sum.pcs, sqm: sum.sqm.toFixed(4) })].join(' · ')}
          </Typography>
        </Box>

        {/* On a phone the controls take a row of their own under the title, 40px each, as on a packing list. */}
        <Box data-testid="lf-print-options" sx={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: isXs ? 0.5 : 0.75,
          ...(isXs ? { width: '100%', marginInlineStart: 0, justifyContent: 'space-between' } : {}) }}>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, val) => val && setView(val)}>
            <ToggleButton value="document" aria-label={t('mis.plViewDocument')} data-testid="lf-view-document" sx={{ px: 1, py: 0.25, ...(isXs ? PHONE_ICON : {}) }}>
              <Tooltip title={t('mis.plViewDocument')}><ArticleOutlinedIcon sx={{ fontSize: isXs ? 19 : 15 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="data" aria-label={t('mis.plViewData')} data-testid="lf-view-data" sx={{ px: 1, py: 0.25, ...(isXs ? PHONE_ICON : {}) }}>
              <Tooltip title={t('mis.plViewData')}><ViewListIcon sx={{ fontSize: isXs ? 19 : 15 }} /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Select size="small" value={lang} onChange={(e) => setLang(e.target.value)} inputProps={{ 'aria-label': t('mis.lblLanguage') }}
            SelectDisplayProps={{ 'data-testid': 'lf-language' }}
            sx={{ height: isXs ? 40 : 28, fontSize: isXs ? '0.8rem' : '0.72rem', minWidth: isXs ? 96 : 86, ...(isXs ? { flex: 1, maxWidth: 140 } : {}) }}>
            <MenuItem value="en" sx={{ fontSize: '0.78rem' }}>English</MenuItem>
            <MenuItem value="ar" sx={{ fontSize: '0.78rem' }}>العربية</MenuItem>
            <MenuItem value="fa" sx={{ fontSize: '0.78rem' }}>فارسی</MenuItem>
          </Select>

          {canPrint && (isXs ? (
            <Tooltip title={t('mis.lfPrintForm')}>
              <span>
                <IconButton onClick={printDocument} disabled={!docHtml} aria-label={t('mis.lfPrintForm')} data-testid="lf-doc-print" sx={PHONE_ICON}>
                  <PrintIcon sx={{ fontSize: 20, color: T.TEXT_SEC }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button size="small" variant="outlined" onClick={printDocument} disabled={!docHtml} data-testid="lf-doc-print"
              startIcon={<PrintIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.7rem', textTransform: 'none', height: 28 }}>
              {t('mis.lfPrintForm')}
            </Button>
          ))}
          <CopyLinkButton module="mis" entityType="labelForm" entityId={form._id} sx={isXs ? { ...PHONE_ICON, '& svg': { fontSize: 19 } } : undefined} />
          {canEdit && (
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" onClick={() => onEdit(form)} aria-label={t('common.edit')} data-testid="lf-edit" sx={isXs ? PHONE_ICON : undefined}>
                <EditIcon sx={{ fontSize: isXs ? 19 : 16, color: T.TEXT_SEC }} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t('mis.lfDelete')}>
              <IconButton size="small" onClick={() => onDelete(form)} aria-label={t('mis.lfDelete')} data-testid="lf-delete" sx={isXs ? PHONE_ICON : undefined}>
                <DeleteOutlineIcon sx={{ fontSize: isXs ? 19 : 16, color: T.TEXT_SEC }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* the labels no list has yet, as a packing list in one press */}
      {canList && free.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, flex: 1, minWidth: 0 }}>{t('mis.lfFreeNote', { count: free.length, total: pallets.length })}</Typography>
          <Button size="small" variant="outlined" onClick={() => onMakeList(form, free)} data-testid="lf-make-list" startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: { xs: 40, sm: 0 } }}>
            {t('mis.lfMakeList', { count: free.length })}
          </Button>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {view === 'document' ? (
          <>
            {docLoading && !docHtml ? (
              <Skeleton variant="rectangular" height={520} sx={{ borderRadius: '10px' }} />
            ) : docHtml ? (
              <Box data-testid="lf-doc-preview" sx={{ border: `1px solid ${T.BD}`, borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff' }}>
                <DocPreviewFrame html={docHtml} title={`label-form-${form.docNumber}`} height={{ xs: 520, md: 860 }} />
              </Box>
            ) : (
              <Box sx={{ py: 5, textAlign: 'center', border: `1px dashed ${T.BD}`, borderRadius: '10px' }}>
                <DescriptionIcon sx={{ fontSize: 28, color: T.TEXT_TER, mb: 0.5 }} />
                <Typography sx={{ fontSize: '0.75rem', color: T.TEXT_TER }}>{t('mis.previewUnavailable')}</Typography>
              </Box>
            )}

            {/* The labels sit under the document, where the pallets they are for are listed, as under a packing list. */}
            {canPrint && pallets.length > 0 && (
              <Box data-testid="lf-labels" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography sx={sectionLabel}>{t('mis.lfLabelsSection')}</Typography>
                  <Box sx={{ marginInlineStart: 'auto', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => setSheetOpen(true)} data-testid="lf-short-sheet-open"
                      startIcon={<CropLandscapeOutlinedIcon sx={{ fontSize: 14 }} />} sx={labelButton}>
                      {t('mis.plShortSheetButton')}
                    </Button>
                    <Button size="small" variant="contained" onClick={() => setPrintAllOpen(true)} data-testid="lf-labels-print-all"
                      startIcon={<PrintIcon sx={{ fontSize: 14 }} />} sx={labelButton}>
                      {t('mis.lfPrintAll', { count: ids.length })}
                    </Button>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC, mb: 1.25 }}>{t('mis.lfLabelsHelp')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  {pallets.map((p, i) => (
                    <PalletLabelCard key={p._id} pallet={p} index={i} lang={lang} onPrint={printOne(p)} />
                  ))}
                </Box>
              </Box>
            )}
            {!pallets.length && (
              <Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC, textAlign: 'center', py: 3 }}>{t('mis.lfNoPallets')}</Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: isXs ? '1fr 1fr' : 'repeat(auto-fill, minmax(190px, 1fr))',
              p: 1.5, mb: 2, border: `1px solid ${T.BD}`, borderRadius: '10px', bgcolor: T.CTRL_BG }}>
              {meta.map(([label, value, mono]) => <Meta key={label} label={label} value={value} mono={mono} T={T} />)}
            </Box>
            <Box sx={{ border: `1px solid ${T.BD}`, borderRadius: '12px', px: 1.5, py: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: isXs ? 0.75 : 1, pb: 0.5, alignItems: 'end', borderBottom: `1px solid ${T.BD}` }}>
                {isXs
                  ? [head(t('mis.referenceField')), head(t('mis.palletIdField')), head(t('mis.plColSqm'), true), head(t('mis.lfStateColumn'))]
                  : [head(t('mis.referenceField')), head(t('mis.palletIdField')), head(t('mis.processingTypeField')), head(t('mis.lfRowsColumn'), true),
                    head(t('mis.plColPcs'), true), head(t('mis.plColSqm'), true), head(t('mis.lfStateColumn'))]}
              </Box>
              {pallets.map((p, i) => {
                const s = sumOf([p]);
                const final = Boolean(p.list && p.list.status === 'final');
                const color = colors[!p.list ? 'available' : final ? 'final' : 'onList'];
                const state = !p.list ? t('mis.lblAvailable') : t(final ? 'mis.lblFinalChip' : 'mis.lblOnListChip', { list: p.list.docNumber });
                return (
                  <Box key={p._id} data-lf-pallet={p.reference} onClick={() => onOpenPallet(p._id)}
                    sx={{ display: 'grid', gridTemplateColumns: cols, gap: isXs ? 0.75 : 1, py: 0.6, alignItems: 'center', cursor: 'pointer',
                      bgcolor: i % 2 ? T.ROW_ALT : 'transparent', borderRadius: '6px', '&:hover': { bgcolor: T.CTRL_BG } }}>
                    <Typography dir="ltr" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI, textAlign: 'start', minWidth: 0 }}>{p.reference}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, minWidth: 0 }}>{p.palletId || '—'}</Typography>
                    {!isXs && <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, minWidth: 0 }}>{p.processingType || '—'}</Typography>}
                    {!isXs && <Typography sx={numberCell}>{(p.items || []).length}</Typography>}
                    {!isXs && <Typography sx={numberCell}>{s.pcs}</Typography>}
                    <Typography sx={{ ...numberCell, fontWeight: 600, color: T.TEXT_PRI }}>{s.sqm.toFixed(4)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      {p.list ? (
                        <Typography noWrap component="span" onClick={(e) => { e.stopPropagation(); onOpenList(p.list._id); }} data-lf-state="onList"
                          sx={{ fontSize: '0.68rem', fontWeight: 700, color, textDecoration: 'underline', textUnderlineOffset: 2 }}>{state}</Typography>
                      ) : (
                        <Typography noWrap data-lf-state="available" sx={{ fontSize: '0.68rem', fontWeight: 700, color }}>{state}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 0.75, mt: 0.5, borderTop: `1px solid ${T.BD}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI }}>{t('mis.lfLabelCount', { count: pallets.length })}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI }}>{sum.pcs} {t('mis.plColPcs')}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI }}>{sum.sqm.toFixed(4)} {t('mis.plColSqm')}</Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>

      <PalletPrintDialog open={printAllOpen} onClose={() => setPrintAllOpen(false)} ids={ids} branchId={form.branchId} />
      <ShortLabelSheetDialog open={sheetOpen} onClose={() => setSheetOpen(false)} docNumber={form.docNumber} pallets={pallets}
        keyOf={byRecord} onPrint={printSheet} />
    </Box>
  );
}
